import { OpenInAnalyticsLink } from "../../../../../components/analytics/OpenInAnalyticsLink";
import {
  formatClockTime,
  formatLongCalendarDate,
} from "../../../../../components/audit/auditLog/auditDates";
import { Badge } from "../../../../../components/ui/badge";
import { UNREADABLE_LINK_RESPONSE } from "../../../../../hooks/audit/useAnalyticsWorkflowVersionLink";
import type { AnalyticsFailureKind } from "../../../../../lib/analyticsWorkflow";
import { PaneSection } from "./PaneSection";

/**
 * What a minted `null` says.
 *
 * The version and not the workflow: this link is addressed to the version that
 * produced the result on screen, so a workflow that is alive and openable can
 * still answer nothing here, and a line about the workflow would send an
 * auditor looking for a deletion that has not happened.
 */
const VERSION_CANNOT_BE_OPENED =
  "The version this test ran cannot be opened in Optro Analytics.";

/**
 * Every kind, worded for a reviewer reading a recorded result rather than an
 * author building one: the shared defaults say "this workflow" and assume the
 * reader can act on the workspace. Exhaustive so a new kind cannot reach this
 * pane in the author's words.
 */
const VERSION_LINK_FAILURE_MESSAGES: Record<AnalyticsFailureKind, string> = {
  // Never answered here, since a deleted workflow mints `null`; worded only
  // because the map is exhaustive.
  workflow_deleted:
    "The workflow behind this result has been deleted, so the version this test ran can no longer be opened in Optro Analytics. The result stands as recorded.",
  // Named for the version, as the `null` answer is, but leaving the cause
  // open: the request reached Midship, and what it found missing is not
  // limited to the run behind this result — the workspace's integration is
  // one such.
  not_found:
    "The version this test ran could not be opened in Optro Analytics because something it needs could not be found. If this keeps happening, ask an administrator to check this workspace's Optro integration.",
  permission_denied:
    "Access to the version this test ran was denied, so it could not be opened in Optro Analytics. The result stands as recorded.",
  conflict:
    "The version this test ran could not be opened because this workspace has no active Optro Analytics credential. The result stands as recorded.",
  rejected:
    "The request for the version this test ran was not accepted, so it could not be opened in Optro Analytics. Reload the page and try again.",
  session_expired:
    "Your session ended before the version this test ran could be opened. Sign in again to continue.",
  upstream_unavailable:
    "The version this test ran could not be opened in Optro Analytics because a service it depends on did not answer. The result stands as recorded; try again later.",
  unreachable:
    "No response arrived, so the version this test ran could not be opened in Optro Analytics. Check your connection and try again.",
  unreadable: UNREADABLE_LINK_RESPONSE,
  unknown:
    "The version this test ran could not be opened in Optro Analytics because of an unexpected error. The result stands as recorded.",
};

interface WorkflowLinkProps {
  deleted?: false;
  /**
   * The workflow's name as the live status read reports it, or null, which
   * leaves the name line out.
   *
   * Never the name stored on the column: that one is the label captured at
   * authoring time, and after an upstream rename it names a workflow other than
   * the one the link opens.
   */
  name: string | null;
  /**
   * The label AuditBoard shows for the version the run executed, such as `"3"`,
   * as recorded with the run; null where none was recorded.
   *
   * Never the version's id: that is a row id shared across every workflow in the
   * tenant, and naming it as a version number contradicts the one an auditor
   * reads in Optro. Null leaves the version out rather than falling back to the
   * id.
   */
  version: string | null;
  /**
   * When the run ran, per `verdictRunDate`, or null where no step of it is
   * recorded finishing; the run time is then left out rather than guessed.
   */
  runDate: Date | null;
  /**
   * Mints the address of that version, at the moment of the click. Answers
   * `null` where there is no page for it.
   */
  onOpen: () => Promise<string | null>;
}

interface WorkflowDeletedProps {
  deleted: true;
  /**
   * The bound workflow's id, or null where the column records no binding.
   *
   * The id and never a name, for the reason the deleted-state paragraph names
   * it that way: the only name Midship holds is free text from authoring time,
   * and the live lookup that would supply the real one is the call answering
   * `410 Gone` here.
   */
  workflowId: number | null;
  /**
   * The label AuditBoard showed for the version the run executed, as above.
   * Recorded with the run, so it survives the workflow's deletion; null where
   * none was recorded.
   */
  version: string | null;
  /**
   * That the workflow behind this result can no longer be reached, and, where
   * the server stated it, when the run produced it and over how many rows.
   */
  recordedAgainst: string;
}

export type WorkflowSectionProps = WorkflowLinkProps | WorkflowDeletedProps;

/**
 * The way out to the workflow itself, in three lines: its name, the version
 * the run executed and when it ran, and the link.
 *
 * **No workflow graph is drawn here and none should be.** The build definition
 * a graph needs reaches no endpoint Midship can call, and it describes the
 * workflow as it stands now rather than the version that ran — so on the one
 * pane a reader consults because they do not know what happened, a graph would
 * confidently show a shape the run never had. The link says less and cannot
 * mislead.
 *
 * **A button that fetches on the click, never an `href`.** The address carries
 * a Cascade session credential minted for the acting user, and the status read
 * beside this section is polled; an `href` would hold that credential in the
 * page between clicks and re-mint it on every poll. A version with no page to
 * open is then said in a sentence, because a click that resolves to nothing is
 * indistinguishable from a broken one.
 *
 * **A deleted workflow keeps this section, as the record of what produced the
 * result.** It is the one state that takes the other arm: the workflow is named
 * with its version and marked deleted, with no link, because the result stands
 * as recorded while the thing that produced it is gone — and removing the
 * section there would leave the conclusion with nothing saying what ran it.
 */
export function WorkflowSection(props: WorkflowSectionProps) {
  if (props.deleted === true) {
    const { workflowId, version, recordedAgainst } = props;
    // Each half is stated only where it is known, so neither prints a label
    // with nothing after it. Both absent leaves the chip, which is still the
    // fact this section exists to record.
    const identity = [
      workflowId === null ? null : `Workflow ${workflowId}`,
      version === null ? null : `version ${version}`,
    ]
      .filter((part): part is string => part !== null)
      .join(" · ");

    return (
      <PaneSection title="WORKFLOW">
        <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
          {identity !== "" && <span>{identity}</span>}
          <Badge variant="secondary">DELETED</Badge>
        </div>
        <p className="text-xs text-font-secondary">{recordedAgainst}</p>
      </PaneSection>
    );
  }

  const { name, version, runDate, onOpen } = props;
  const versionAndRunTime = [
    version === null ? null : `Version ${version}`,
    runDate === null
      ? null
      : `Ran ${formatLongCalendarDate(runDate)}, ${formatClockTime(runDate).toLowerCase()}`,
  ]
    .filter((part): part is string => part !== null)
    .join(" · ");

  return (
    <PaneSection title="WORKFLOW">
      {name !== null && <p className="text-sm">{name}</p>}
      {versionAndRunTime !== "" && (
        <p className="text-xs text-font-secondary">{versionAndRunTime}</p>
      )}
      <OpenInAnalyticsLink
        onOpen={onOpen}
        nothingToOpen={VERSION_CANNOT_BE_OPENED}
        failureMessages={VERSION_LINK_FAILURE_MESSAGES}
        className="text-sm"
      />
    </PaneSection>
  );
}
